import React, { ReactNode } from 'react';
import {
  Box,
  BoxProps,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from '@chakra-ui/react';
import { MagnifyingGlass } from 'phosphor-react';

// Peças visuais da Central de Permissões (layout do design aprovado em 22/10).

export const AZUL = '#0077B6';
export const AZUL_ESCURO = '#006D9E';
export const TEXTO = '#0F172A';
export const TEXTO_SUAVE = '#5B6676';
export const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

export const Cartao = ({ children, ...props }: BoxProps & { children: ReactNode }) => (
  <Box
    bg="rgba(255,255,255,.78)"
    borderRadius="22px"
    border="1px solid rgba(255,255,255,.75)"
    boxShadow="0 1px 0 rgba(255,255,255,.9) inset, 0 18px 40px -26px rgba(15,23,42,.35)"
    backdropFilter="blur(10px)"
    {...props}
  >
    {children}
  </Box>
);

export const Eyebrow = ({
  children,
  color = TEXTO_SUAVE,
}: {
  children: ReactNode;
  color?: string;
}) => (
  <Text fontFamily={MONO} fontSize="10.5px" fontWeight={500} letterSpacing="1px" color={color}>
    {children}
  </Text>
);

export const Contador = ({
  children,
  tom = 'azul',
}: {
  children: ReactNode;
  tom?: 'azul' | 'ambar';
}) => (
  <Box
    as="span"
    fontFamily={MONO}
    fontSize="11px"
    fontWeight={600}
    borderRadius="999px"
    px="8px"
    py="1px"
    color={tom === 'azul' ? '#075985' : '#92400E'}
    bg={tom === 'azul' ? '#DFF3FF' : '#FEF3C7'}
    whiteSpace="nowrap"
  >
    {children}
  </Box>
);

export const Pilula = ({
  ativa,
  onClick,
  children,
  contagem,
}: {
  ativa: boolean;
  onClick: () => void;
  children: ReactNode;
  contagem?: number;
}) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    aria-pressed={ativa}
    h="34px"
    px="14px"
    borderRadius="999px"
    fontSize="13px"
    fontWeight={600}
    display="inline-flex"
    alignItems="center"
    gap="6px"
    color={ativa ? '#FFFFFF' : '#334155'}
    bg={ativa ? `linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})` : 'rgba(255,255,255,.6)'}
    boxShadow={ativa ? '0 6px 16px -4px rgba(0,109,158,.45)' : 'inset 0 0 0 1px rgba(15,23,42,.08)'}
    transition="background .2s"
    _hover={ativa ? undefined : { bg: 'rgba(255,255,255,.95)' }}
  >
    {children}
    {contagem !== undefined && (
      <Box
        as="span"
        fontFamily={MONO}
        fontSize="11px"
        px="6px"
        borderRadius="999px"
        bg={ativa ? 'rgba(255,255,255,.22)' : '#E2E8F0'}
      >
        {contagem}
      </Box>
    )}
  </Box>
);

export const AlternarDois = ({
  opcoes,
  valor,
  onChange,
}: {
  opcoes: { valor: string; rotulo: string }[];
  valor: string;
  onChange: (v: string) => void;
}) => (
  <Flex
    p="4px"
    gap="2px"
    bg="rgba(255,255,255,.55)"
    borderRadius="999px"
    boxShadow="inset 0 0 0 1px rgba(15,23,42,.06)"
  >
    {opcoes.map(o => {
      const ativa = o.valor === valor;
      return (
        <Box
          key={o.valor}
          as="button"
          type="button"
          flex={1}
          h="32px"
          borderRadius="999px"
          fontSize="13px"
          fontWeight={600}
          color={ativa ? '#FFFFFF' : '#475569'}
          bg={ativa ? `linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})` : 'transparent'}
          boxShadow={ativa ? '0 6px 16px -4px rgba(0,109,158,.45)' : undefined}
          onClick={() => onChange(o.valor)}
        >
          {o.rotulo}
        </Box>
      );
    })}
  </Flex>
);

export const Busca = ({
  valor,
  onChange,
  placeholder,
  maxW,
}: {
  valor: string;
  onChange: (v: string) => void;
  placeholder: string;
  maxW?: string;
}) => (
  <InputGroup maxW={maxW}>
    <InputLeftElement pointerEvents="none" h="40px">
      <MagnifyingGlass size={16} color={TEXTO_SUAVE} />
    </InputLeftElement>
    <Input
      type="search"
      h="40px"
      borderRadius="10px"
      bg="rgba(255,255,255,.85)"
      borderColor="#CBD5E1"
      fontSize="14px"
      placeholder={placeholder}
      aria-label={placeholder}
      value={valor}
      onChange={e => onChange(e.target.value)}
    />
  </InputGroup>
);

export const Etiqueta = ({
  tom,
  children,
}: {
  tom: 'info' | 'aviso' | 'sucesso' | 'neutro' | 'perigo';
  children: ReactNode;
}) => {
  const cores = {
    info: ['#DFF3FF', '#075985'],
    aviso: ['#FEF3C7', '#92400E'],
    sucesso: ['#DCFCE7', '#166534'],
    neutro: ['#F1F5F9', '#475569'],
    perigo: ['#FEE2E2', '#991B1B'],
  }[tom];
  return (
    <Box
      as="span"
      fontSize="11px"
      fontWeight={600}
      px="8px"
      py="1px"
      borderRadius="999px"
      bg={cores[0]}
      color={cores[1]}
      whiteSpace="nowrap"
    >
      {children}
    </Box>
  );
};

export const Interruptor = ({
  ligado,
  onChange,
  rotulo,
  desabilitado,
}: {
  ligado: boolean;
  onChange: () => void;
  rotulo: string;
  desabilitado?: boolean;
}) => (
  <Box
    as="button"
    type="button"
    role="switch"
    aria-checked={ligado}
    aria-label={rotulo}
    disabled={desabilitado}
    onClick={onChange}
    w="38px"
    h="22px"
    flex="none"
    borderRadius="999px"
    position="relative"
    bg={ligado ? AZUL : '#CBD5E1'}
    opacity={desabilitado ? 0.5 : 1}
    cursor={desabilitado ? 'not-allowed' : 'pointer'}
    transition="background .3s"
  >
    <Box
      position="absolute"
      top="3px"
      left="3px"
      w="16px"
      h="16px"
      borderRadius="50%"
      bg="#FFFFFF"
      boxShadow="0 1px 3px rgba(15,23,42,.25)"
      transform={ligado ? 'translateX(16px)' : 'none'}
      transition="transform .45s cubic-bezier(.34,1.56,.64,1)"
    />
  </Box>
);

export const Marcador = ({
  marcado,
  onChange,
  rotulo,
  alterado,
}: {
  marcado: boolean;
  onChange: () => void;
  rotulo: string;
  alterado?: boolean;
}) => (
  <Flex
    justify="center"
    align="center"
    h="30px"
    borderRadius="10px"
    bg={alterado ? '#FEF3C7' : undefined}
    boxShadow={alterado ? 'inset 0 0 0 1px #FDE68A' : undefined}
  >
    <input
      type="checkbox"
      checked={marcado}
      onChange={onChange}
      aria-label={rotulo}
      style={{ width: 18, height: 18, accentColor: AZUL, cursor: 'pointer' }}
    />
  </Flex>
);

export const BarraSalvar = ({
  texto,
  semMudanca,
  salvando,
  onDesfazer,
  onSalvar,
}: {
  texto: string;
  semMudanca: boolean;
  salvando: boolean;
  onDesfazer: () => void;
  onSalvar: () => void;
}) => (
  <Cartao
    position="sticky"
    bottom="16px"
    zIndex={2}
    display="flex"
    alignItems="center"
    gap="12px"
    flexWrap="wrap"
    p="12px 16px 12px 20px"
    bg="rgba(255,255,255,.9)"
  >
    <Text flex="1 1 260px" fontSize="13px" lineHeight="18px" color="#334155">
      {texto}
    </Text>
    <Button
      variant="ghost"
      size="sm"
      borderRadius="999px"
      isDisabled={semMudanca || salvando}
      onClick={onDesfazer}
    >
      Desfazer
    </Button>
    <Button
      size="sm"
      borderRadius="999px"
      color="white"
      bg={`linear-gradient(180deg, ${AZUL}, ${AZUL_ESCURO})`}
      _hover={{ bg: AZUL_ESCURO }}
      isDisabled={semMudanca}
      isLoading={salvando}
      onClick={onSalvar}
    >
      Salvar alterações
    </Button>
  </Cartao>
);
